import { stripAtomMetadata } from "@atoms/atom-types";
import { normalizeRollerUserOptionForImport } from "../rollerOptionUtils";

export const removeDeletedFieldsDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(removeDeletedFieldsDeep);
  }
  if (typeof value !== "object" || value === null) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (key === "deleted") continue;
    result[key] = removeDeletedFieldsDeep(nested);
  }
  return result;
};

export const stripDbMetaFields = (
  option: Record<string, unknown>,
): Record<string, unknown> => {
  // Recursive: nested internals (e.g. `__schemaVersion` on embedded option
  // atoms) must go too, otherwise exported/compared options differ from what
  // the backend would ever store.
  const cleaned = stripAtomMetadata(option);
  return removeDeletedFieldsDeep(cleaned) as Record<string, unknown>;
};

const isDeletedOption = (option: Record<string, unknown>): boolean =>
  option.deleted === true;

/**
 * The user options as a configurator's `user-options.json` (DB-first):
 * - source is the persisted user options list from DB
 * - deleted entries are excluded
 * - DB technical metadata fields are removed
 * - id is kept as the UserOption entity UUID for stable option references
 *
 * Shared by the browser download and the backend export.
 */
export const exportAllOptions = (
  fullUserOptionsData: readonly unknown[] | null | undefined,
): Record<string, unknown>[] => {
  if (!Array.isArray(fullUserOptionsData)) return [];

  const exportedOptions: Record<string, unknown>[] = [];
  for (const entry of fullUserOptionsData) {
    if (typeof entry !== "object" || entry === null) continue;
    const option = entry as Record<string, unknown>;
    if (isDeletedOption(option)) continue;
    exportedOptions.push(
      normalizeRollerUserOptionForImport(stripDbMetaFields(option)),
    );
  }
  return exportedOptions;
};
